import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Stack,
  Grid,
  Alert,
  Autocomplete,
  AutocompleteOption,
} from '@mui/joy';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { BoardStatus, ParsedAddress } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { TagPicker } from '../tags/TagPicker';
import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import { applyPrefill, contactAddressFields } from '../../utils/prefill';
import { useContact } from '../../hooks/useCrm';

interface AddJobModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedBoard?: number;
  selectedStatus?: number;
  /**
   * Seeds the build date, so opening this from a calendar day means that day
   * is already filled in. Stays editable like every other prefill.
   */
  defaultBuildDate?: string;
  /**
   * Preselects this contact and seeds the job address from it — used when the
   * modal is opened from a contact's page. Stays editable like every prefill.
   */
  defaultContactId?: number;
}

interface JobFormData {
  name?: string;
  claim_number?: string;
  gate_code?: string;
  gated_community?: boolean | null;
  insurance_company?: string;
  policy_number?: string;
  deductible_amount?: string;
  date_of_loss?: string;
  adjuster_name?: string;
  adjuster_email?: string;
  adjuster_phone?: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  zip_code: string;
  value?: number;
  confidence?: number;
  board: number;
  board_status: number;
  associated_contact?: number;
  build_date?: string;
  tag_ids?: number[];
}

const EMPTY_FORM: JobFormData = {
  name: '',
  claim_number: '',
  insurance_company: '',
  policy_number: '',
  deductible_amount: '',
  date_of_loss: '',
  adjuster_name: '',
  adjuster_email: '',
  adjuster_phone: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip_code: '',
  value: undefined,
  confidence: undefined,
  board: 0,
  board_status: 0,
  associated_contact: undefined,
  build_date: undefined,
  tag_ids: [],
};

/**
 * The list endpoint sends `full_name`; the detail endpoint (used for
 * `defaultContactId`) only sends first/last.
 */
const contactName = (contact: { id: number; full_name?: string | null; first_name?: string | null; last_name?: string | null }): string =>
  contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || `Contact ${contact.id}`;

/** The job address fields seeded from a contact's primary address (none if it has no address). */
const jobAddressFrom = (contact: Parameters<typeof contactAddressFields>[0]): Partial<JobFormData> => {
  const address = contactAddressFields(contact);
  if (!address) return {};
  return {
    address_1: address.address_1,
    address_2: address.address_2,
    city: address.city,
    state: address.state,
    zip_code: address.zip_code,
  };
};

export const AddJobModal: React.FC<AddJobModalProps> = ({
  open,
  onClose,
  onSuccess,
  selectedBoard,
  selectedStatus,
  defaultBuildDate,
  defaultContactId,
}) => {
  const { user } = useAuth();
  const [dirty, setDirty] = React.useState(false);
  const confirmClose = () => { if (!dirty || window.confirm('Discard unsaved job changes?')) { setDirty(false); onClose(); } };
  React.useEffect(() => { if (open) setDirty(false); }, [open]);
  React.useEffect(() => {
    if (!open || !dirty) return;
    const protect = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [open, dirty]);
  const [formData, setFormData] = useState<JobFormData>({
    ...EMPTY_FORM,
    board: selectedBoard || 0,
    board_status: selectedStatus || 0,
  });

  const [contactSearch, setContactSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Fetch all tags
  const { data: availableTags = [], refetch: refetchTags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await apiClient.getTags();
      return response.data;
    },
  });

  // Handle creating new tags
  const handleCreateTag = async (name: string, color: string) => {
    const response = await apiClient.createTag(name, color);
    await refetchTags(); // Refresh the tags list
    return response.data;
  };

  // Update tag_ids when selectedTagIds changes
  React.useEffect(() => {
    setFormData(prev => ({ ...prev, tag_ids: selectedTagIds }));
  }, [selectedTagIds]);

  // Reset form when modal opens/closes or selected board changes
  React.useEffect(() => {
    if (open) {
      setFormData({
        ...EMPTY_FORM,
        board: selectedBoard || 0,
        board_status: selectedStatus || 0,
        build_date: defaultBuildDate,
      });
      setContactSearch('');
      setSelectedTagIds([]);
    }
  }, [open, selectedBoard, selectedStatus, defaultBuildDate]);

  // Fetch boards for selection
  const { data: boards = [] } = useQuery({
    queryKey: ['boards'],
    queryFn: async () => {
      const response = await apiClient.getBoards();
      return response.data;
    },
  });

  // Fetch statuses for selected board
  const { data: statuses = [] } = useQuery({
    queryKey: ['board-statuses', formData.board],
    queryFn: async () => {
      if (!formData.board) return [];
      const response = await apiClient.getAllPages<BoardStatus>(`/api/board-statuses/by-board/${formData.board}/`);
      // Ensure we always return an array
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!formData.board,
  });

  // Fetch contacts for dropdown with search
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', contactSearch],
    queryFn: async () => {
      let url = `/api/crm/contacts/`;
      const params = [];
      
      if (contactSearch) {
        params.push(`search=${encodeURIComponent(contactSearch)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await apiClient.get<any>(url);
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetched by id (shares the contact page's cache): the search results above
  // are one page and may not include this contact, and the picker looks its
  // value up in its options.
  const { data: defaultContact, isError: defaultContactError } = useContact(
    open && defaultContactId ? defaultContactId : 0,
  );
  const contactOptions = React.useMemo(
    () =>
      defaultContact && !contacts.some((contact: { id: number }) => contact.id === defaultContact.id)
        ? [defaultContact, ...contacts]
        : contacts,
    [contacts, defaultContact],
  );

  // Runs after the open-reset above, so it seeds the fresh form. Not marked
  // dirty — closing without edits shouldn't prompt to discard a prefill.
  React.useEffect(() => {
    if (!open || !defaultContact) return;
    setFormData(prev => applyPrefill(
      prev,
      { associated_contact: defaultContact.id, ...jobAddressFrom(defaultContact) },
    ));
  }, [open, defaultContact]);

  // Create job mutation
  const createMutation = useMutation({
    mutationFn: async (data: JobFormData) => {
      // Remove organization field as it's auto-assigned by the backend
      const { ...jobData } = data;
      const response = await apiClient.post('/api/jobs/', jobData);
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      // Handle error silently
    },
  });

  const changeFormData: typeof setFormData = (value) => { setDirty(true); setFormData(value); };
  const handleAddressSelect = (parsed: ParsedAddress) => {
    changeFormData(prev => ({
      ...prev,
      address_1: parsed.address_1 || prev.address_1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zip_code: parsed.zip_code || prev.zip_code,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.address_1 || !formData.city || !formData.state || !formData.zip_code || !formData.board || !formData.board_status) {
      return;
    }

    // Clean up the data before sending - remove undefined values and empty strings
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as JobFormData);
    cleanedData.name = formData.name?.trim() || '';

    // Add tags to the submission
    if (selectedTagIds.length > 0) {
      cleanedData.tag_ids = selectedTagIds;
    }

    console.log('[AddJobModal] Submitting job with data:', {
      ...cleanedData,
      tag_ids: cleanedData.tag_ids
    });

    createMutation.mutate(cleanedData);
  };

  // Helper function to format error messages
  const getErrorMessage = (error: any): string => {
    if (error?.responseData) {
      // Handle Django validation errors
      const errors = error.responseData;
      const messages: string[] = [];
      
      Object.entries(errors).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          messages.push(`${field}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          messages.push(`${field}: ${value}`);
        } else {
          messages.push(`${field}: ${JSON.stringify(value)}`);
        }
      });
      
      return messages.join('\n');
    }
    
    return error?.message || 'An error occurred while creating the job';
  };

  // US states for dropdown
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const isFormValid = formData.address_1 && formData.city && formData.state && formData.zip_code && formData.board && formData.board_status;

  return (
    <Modal open={open} onClose={confirmClose}>
      <ModalDialog size="lg" sx={{ overflow: 'auto', maxHeight: '90vh' }}>
        <ModalClose />
        <Typography level="h4">Add New Job</Typography>
        
        {createMutation.isError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {getErrorMessage(createMutation.error)}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Stack direction="row" spacing={1} sx={{flexWrap:'wrap',my:1}}>{['Property','People','Claim'].map(section=><Button key={section} type="button" variant="soft" size="sm" onClick={()=>document.getElementById(`add-job-${section.toLowerCase()}`)?.scrollIntoView({block:'start',behavior:'smooth'})}>{section}</Button>)}</Stack>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Basic Claim and job information */}
            <Typography id="add-job-claim" level="title-md" sx={{ mt: 2 }}>
              Claim and job information
            </Typography>
            
            <FormControl>
              <FormLabel>Job Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => changeFormData({ ...formData, name: e.target.value })}
                placeholder="Enter job name (optional)"
                slotProps={{ input: { maxLength: 255 } }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Claim Number</FormLabel>
              <Input
                value={formData.claim_number}
                onChange={(e) => changeFormData({ ...formData, claim_number: e.target.value })}
                placeholder="Enter claim number (optional)"
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}><FormControl><FormLabel>Gate Code</FormLabel>
                <Input value={formData.gate_code || ''} onChange={(e) => changeFormData({ ...formData, gate_code: e.target.value })} />
              </FormControl></Grid>
              <Grid xs={12} sm={6}><FormControl><FormLabel>Gated Community</FormLabel>
                <Select value={formData.gated_community == null ? '' : String(formData.gated_community)}
                  onChange={(_, value) => changeFormData({ ...formData, gated_community: !value ? null : value === 'true' })}>
                  <Option value="">Not specified</Option><Option value="true">Yes</Option><Option value="false">No</Option>
                </Select>
              </FormControl></Grid>
            </Grid>

            {/* Claim details - stored on the job so the supplement and CoC
                documents can print it instead of asking for it again */}
            <Typography level="title-md" sx={{ mt: 2 }}>
              Claim details
            </Typography>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Insurance Company</FormLabel>
                  <Input
                    value={formData.insurance_company}
                    onChange={(e) => changeFormData({ ...formData, insurance_company: e.target.value })}
                    placeholder="Carrier name (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Policy Number</FormLabel>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => changeFormData({ ...formData, policy_number: e.target.value })}
                    placeholder="Policy number (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Deductible ($)</FormLabel>
                  <Input
                    type="number"
                    value={formData.deductible_amount}
                    onChange={(e) => changeFormData({ ...formData, deductible_amount: e.target.value })}
                    placeholder="0.00"
                    slotProps={{
                      input: {
                        min: 0,
                        step: 0.01,
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Date of Loss</FormLabel>
                  <Input
                    type="date"
                    value={formData.date_of_loss}
                    onChange={(e) => changeFormData({ ...formData, date_of_loss: e.target.value })}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Name</FormLabel>
                  <Input
                    value={formData.adjuster_name}
                    onChange={(e) => changeFormData({ ...formData, adjuster_name: e.target.value })}
                    placeholder="Adjuster name (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.adjuster_email}
                    onChange={(e) => changeFormData({ ...formData, adjuster_email: e.target.value })}
                    placeholder="adjuster@carrier.com"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Phone</FormLabel>
                  <Input
                    value={formData.adjuster_phone}
                    onChange={(e) => changeFormData({ ...formData, adjuster_phone: e.target.value })}
                    placeholder="(555) 555-5555"
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Address Information */}
            <Typography id="add-job-property" level="title-md" sx={{ mt: 2 }}>
              Property
            </Typography>

            <FormControl required>
              <FormLabel>Street Address</FormLabel>
              <AddressAutocompleteInput
                value={formData.address_1}
                onChange={(val) => changeFormData({ ...formData, address_1: val })}
                onAddressSelect={handleAddressSelect}
                placeholder="Enter street address"
                required
              />
            </FormControl>

            <FormControl>
              <FormLabel>Address Line 2</FormLabel>
              <Input
                value={formData.address_2}
                onChange={(e) => changeFormData({ ...formData, address_2: e.target.value })}
                placeholder="Apartment, suite, etc. (optional)"
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={formData.city}
                    onChange={(e) => changeFormData({ ...formData, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={3}>
                <FormControl required>
                  <FormLabel>State</FormLabel>
                  <Select
                    value={formData.state || null}
                    onChange={(_, value) => changeFormData({ ...formData, state: value || '' })}
                    placeholder="Select state"
                  >
                    {usStates.map((state) => (
                      <Option key={state} value={state}>
                        {state}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={3}>
                <FormControl required>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => changeFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="ZIP code"
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Job Details */}
            <Typography level="title-md" sx={{ mt: 2 }}>
              Job Details
            </Typography>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Board</FormLabel>
                  <Select
                    value={formData.board !== 0 ? formData.board : null}
                    onChange={(_, value) => {
                      changeFormData({
                        ...formData, 
                        board: value || 0,
                        board_status: 0 // Reset status when board changes
                      });
                    }}
                    placeholder="Select board"
                  >
                    {(boards || []).map((board) => (
                      <Option key={board.id} value={board.id}>
                        {board.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.board_status !== 0 ? formData.board_status : null}
                    onChange={(_, value) => changeFormData({ ...formData, board_status: value || 0 })}
                    placeholder="Select status"
                    disabled={!formData.board}
                  >
                    {(statuses || [])
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((status) => (
                        <Option key={status.id} value={status.id}>
                          {status.name}
                        </Option>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Contract Total ($)</FormLabel>
                  <Input
                    type="number"
                    value={formData.value || ''}
                    onChange={(e) => 
                      changeFormData({
                        ...formData, 
                        value: e.target.value ? parseFloat(e.target.value) : undefined 
                      })
                    }
                    placeholder="0.00"
                    slotProps={{
                      input: {
                        min: 0,
                        step: 0.01,
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Confidence Level (%)</FormLabel>
                  <Input
                    type="number"
                    value={formData.confidence || ''}
                    onChange={(e) => 
                      changeFormData({
                        ...formData, 
                        confidence: e.target.value ? parseInt(e.target.value) : undefined 
                      })
                    }
                    placeholder="0"
                    slotProps={{
                      input: {
                        min: 0,
                        max: 100,
                        step: 1,
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <FormControl>
              <FormLabel>Build Date</FormLabel>
              <Input
                type="date"
                value={formData.build_date || ''}
                onChange={(e) => changeFormData({ ...formData, build_date: e.target.value || undefined })}
                placeholder="Select build date"
                disabled={!user?.is_organization_admin && !user?.can_schedule_builds}
              />
              {!user?.is_organization_admin && !user?.can_schedule_builds && (
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  You don't have permission to schedule build dates
                </Typography>
              )}
            </FormControl>

            {/* Contact Association */}
            <Typography id="add-job-people" level="title-md" sx={{ mt: 2 }}>
              People · contacts
            </Typography>

            <FormControl>
              <FormLabel>Associated Contact</FormLabel>
              <Autocomplete
                placeholder="Search for a contact"
                value={
                  formData.associated_contact
                    ? contactOptions.find((contact: any) => contact.id === formData.associated_contact) || null
                    : null
                }
                inputValue={contactSearch}
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => {
                  // Seed the job site address from the customer's primary
                  // address (carried on the list payload as
                  // primary_address_detail). The site can differ from where
                  // they receive mail, so this only fills fields still empty
                  // and every one stays editable.
                  changeFormData(prev => applyPrefill(
                    { ...prev, associated_contact: value?.id || undefined },
                    jobAddressFrom(value),
                  ));
                }}
                options={contactOptions}
                getOptionLabel={(option: any) => contactName(option)}
                renderOption={(props, option: any) => (
                  <AutocompleteOption {...props} key={option.id}>
                    <div>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {contactName(option)}
                      </Typography>
                      {option.email && (
                        <Typography level="body-xs" textColor="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {option.email}
                        </Typography>
                      )}
                    </div>
                  </AutocompleteOption>
                )}
                sx={{ width: '100%' }}
              />
              {defaultContactError && !formData.associated_contact && (
                <FormHelperText role="alert" sx={{ color: 'warning.plainColor' }}>
                  Couldn't load that contact. Search for it above.
                </FormHelperText>
              )}
            </FormControl>

            {/* Tags */}
            <TagPicker
              value={selectedTagIds}
              onChange={(ids) => { setDirty(true); setSelectedTagIds(ids); }}
              availableTags={availableTags}
              onCreateTag={handleCreateTag}
            />

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4, position: 'sticky', bottom: 0, bgcolor: 'background.surface', py: 2, zIndex: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button variant="outlined" color="neutral" onClick={confirmClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!isFormValid}
              >
                Add Job
              </Button>
            </Stack>
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
};